'use client';

import { useMemo, useEffect } from 'react';

import { getLastTraceId, getNewRelicSessionId } from 'src/components/new-relic/utils';

// Resolve New Relic-native identifiers for the crash:
// - session: the NR browser session id (queryable via `session`, linked to
//   Session Replay, and shared with backend Transactions/Logs).
// - traceId: the last API request's distributed-trace id (a render crash has
//   none of its own; this correlates to backend `trace.id`). May be empty.
// Falls back to a random id only when NR isn't loaded.
function getCrashIds(): { session: string; traceId: string } {
  if (typeof window === 'undefined') return { session: '', traceId: '' };
  let session = '';
  try {
    session = getNewRelicSessionId() ?? '';
  } catch {
    /* NR not available */
  }
  if (!session) {
    session =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);
  }
  return { session, traceId: getLastTraceId() ?? '' };
}

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const { session, traceId } = useMemo(getCrashIds, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.newrelic) {
      window.newrelic.noticeError(error, {
        digest: error.digest ?? '',
        boundary: 'app/error.tsx',
        reference: session,
        traceId,
      });
    }
  }, [error, session, traceId]);

  return (
    <div style={{ padding: 24 }}>
      <h2>Something went wrong.</h2>
      <p>The page crashed. Please try again.</p>
      <p style={{ color: '#666', fontSize: 12 }}>
        If the problem persists, please share these references with support:
        <br />
        Session: <code>{session}</code>
        {traceId && (
          <>
            <br />
            Trace: <code>{traceId}</code>
          </>
        )}
      </p>
      <button type="button" onClick={() => reset()}>
        Try again
      </button>
    </div>
  );
}
